import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, TextField, InputAdornment, IconButton, CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon, PlaylistAdd, Favorite, FavoriteBorder, LibraryAdd, LibraryAddCheck,
  ChevronRight,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  spotifySearch, playSpotifyTrack, queueAdd, spotifyLikeTrack, spotifyUnlikeTrack,
  getSpotifySavedTrackContains, addPlaylistToLibrary,
  saveSpotifyAlbum, removeSpotifyAlbum, getSpotifyAlbumContains,
} from '../../services/sonosApi';
import AddToPlaylistDialog from './AddToPlaylistDialog';
import { TrackArt, TrackPlayButton, spotifyImageUrl, TRACK_ROW_ACTIONS_SX, TRACK_ACTION_ICON_SX } from './sonosTrackUi';
import { SONOS_BLUE, SURFACE_BORDER } from './sonosUiStyles';
import { SonosMessageHandler } from './sonosFeedback';

interface SpotifySearchPanelProps {
  speakerId: number | null;
  crossfade: boolean;
  canControl?: boolean;
  onMessage?: SonosMessageHandler;
  onPlayPlaylist?: (uri: string, name: string) => void;
  /** Si se define, reemplaza playSpotifyTrack / cola Sonos (p. ej. reproducción local). */
  onPlayUri?: (uri: string, opts?: { addToQueue?: boolean }) => Promise<void> | void;
  onQueueUri?: (uri: string) => Promise<void> | void;
  onLibraryChanged?: () => void;
  isDemoUser?: boolean;
  onDemoActionAttempt?: () => void;
}

const TYPE_BADGE_SX = {
  display: 'inline-block',
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: 'uppercase' as const,
  color: SONOS_BLUE,
  bgcolor: 'rgba(35,145,255,0.12)',
  borderRadius: 0.5,
  px: 0.5,
  py: 0.1,
  lineHeight: 1.3,
};

const TypeBadge: React.FC<{ label: string }> = ({ label }) => (
  <Box component="span" sx={TYPE_BADGE_SX}>{label}</Box>
);

const SearchResultRow: React.FC<{
  imageUrl: string | null;
  title: string;
  subtitle?: string;
  onClick?: () => void;
  actions?: React.ReactNode;
}> = ({ imageUrl, title, subtitle, onClick, actions }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      py: 0.75,
      borderBottom: `1px solid ${SURFACE_BORDER}`,
      touchAction: 'pan-y',
      cursor: onClick ? 'pointer' : 'default',
    }}
  >
    <TrackArt url={imageUrl} size={40} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{
        color: '#fff',
        fontSize: 13,
        fontWeight: 600,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{
          color: '#999',
          fontSize: 11,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {actions && (
      <Box sx={TRACK_ROW_ACTIONS_SX} onClick={(e) => e.stopPropagation()}>
        {actions}
      </Box>
    )}
  </Box>
);

const SpotifySearchPanel: React.FC<SpotifySearchPanelProps> = ({
  speakerId,
  crossfade,
  canControl = true,
  onMessage,
  onPlayPlaylist,
  onPlayUri,
  onQueueUri,
  onLibraryChanged,
  isDemoUser = false,
  onDemoActionAttempt,
}) => {
  const blockDemo = () => {
    if (!isDemoUser) return false;
    onDemoActionAttempt?.();
    return true;
  };
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [liked, setLiked] = useState<Set<string>>(new Set());
  const [savedPlaylists, setSavedPlaylists] = useState<Set<string>>(new Set());
  const [cloningPlaylists, setCloningPlaylists] = useState<Set<string>>(new Set());
  const [savedAlbums, setSavedAlbums] = useState<Set<string>>(new Set());
  const [artistFilter, setArtistFilter] = useState<string | null>(null);
  const [addToPlaylistOpen, setAddToPlaylistOpen] = useState(false);
  const [addToPlaylistUri, setAddToPlaylistUri] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearch = useCallback(async (q?: string, type = 'track,album,playlist,artist') => {
    const term = (q ?? query).trim();
    if (!term) {
      setResults(null);
      setSearched(false);
      setSearchError(null);
      setArtistFilter(null);
      return;
    }
    setLoading(true);
    setSearched(true);
    setSearchError(null);
    try {
      const data = await spotifySearch(term, type);
      setResults(data);
    } catch (e: any) {
      setResults(null);
      const detail = e?.response?.data?.detail || t('musicSystem.search.error');
      setSearchError(typeof detail === 'string' ? detail : t('musicSystem.search.error'));
      onMessage?.(typeof detail === 'string' ? detail : t('musicSystem.search.error'), 'error');
    } finally {
      setLoading(false);
    }
  }, [query, onMessage, t]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = query.trim();
    if (term.length < 2) {
      setResults(null);
      setSearched(false);
      setArtistFilter(null);
      return undefined;
    }
    debounceRef.current = setTimeout(() => {
      void handleSearch(term);
    }, 450);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, handleSearch]);

  useEffect(() => {
    const ids = (results?.tracks?.items ?? [])
      .filter(Boolean)
      .map((tr: any) => tr.id)
      .filter(Boolean);
    if (ids.length === 0) return;
    let cancelled = false;
    void (async () => {
      try {
        const contains = await getSpotifySavedTrackContains(ids);
        if (cancelled) return;
        setLiked((prev) => {
          const next = new Set(prev);
          ids.forEach((id: string, i: number) => {
            if (contains[i]) next.add(id);
            else next.delete(id);
          });
          return next;
        });
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [results]);

  useEffect(() => {
    const ids = (results?.albums?.items ?? [])
      .filter(Boolean)
      .map((al: any) => al.id)
      .filter(Boolean);
    if (ids.length === 0) return;
    let cancelled = false;
    void (async () => {
      try {
        const contains = await getSpotifyAlbumContains(ids);
        if (cancelled) return;
        setSavedAlbums((prev) => {
          const next = new Set(prev);
          ids.forEach((id: string, i: number) => {
            if (contains[i]) next.add(id);
            else next.delete(id);
          });
          return next;
        });
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [results]);

  const addPlaylistToLib = async (pl: any) => {
    if (blockDemo()) return;
    if (savedPlaylists.has(pl.id) || cloningPlaylists.has(pl.id)) {
      onMessage?.(t('musicSystem.search.playlistAlreadyInLibrary'), 'info');
      return;
    }
    setCloningPlaylists((prev) => new Set(prev).add(pl.id));
    try {
      const res = await addPlaylistToLibrary(pl.id, {
        uri: pl.uri,
        name: pl.name,
        image_url: spotifyImageUrl(pl),
        owner: pl.owner?.display_name ?? null,
        owner_id: pl.owner?.id ?? null,
        tracks: pl.tracks?.total ?? null,
      });
      setSavedPlaylists((prev) => new Set(prev).add(pl.id));
      if (res.action === 'cloned') {
        if (res.already) {
          onMessage?.(t('musicSystem.search.playlistAlreadyInLibrary'), 'info');
        } else if ((res.count ?? 0) > 0) {
          onMessage?.(t('musicSystem.search.playlistCloned', { count: res.count }), 'success');
        } else {
          onMessage?.(t('musicSystem.search.playlistAddedToLibrary'), 'success');
        }
      } else {
        onMessage?.(t('musicSystem.search.playlistAddedToLibrary'), 'success');
      }
      onLibraryChanged?.();
    } catch (e: any) {
      onMessage?.(e?.response?.data?.detail || t('musicSystem.search.libraryError'), 'error');
    } finally {
      setCloningPlaylists((prev) => { const n = new Set(prev); n.delete(pl.id); return n; });
    }
  };

  const toggleAlbumLibrary = async (al: any) => {
    if (blockDemo()) return;
    const inLib = savedAlbums.has(al.id);
    try {
      if (inLib) {
        await removeSpotifyAlbum([al.id]);
        setSavedAlbums((prev) => { const n = new Set(prev); n.delete(al.id); return n; });
        onMessage?.(t('musicSystem.search.albumRemovedFromLibrary'), 'success');
      } else {
        await saveSpotifyAlbum([al.id]);
        setSavedAlbums((prev) => new Set(prev).add(al.id));
        onMessage?.(t('musicSystem.search.albumAddedToLibrary'), 'success');
      }
      onLibraryChanged?.();
    } catch (e: any) {
      onMessage?.(e?.response?.data?.detail || t('musicSystem.search.libraryError'), 'error');
    }
  };

  const playTrack = async (uri: string, addToQueue = false) => {
    if (blockDemo()) return;
    if (!canControl) {
      onMessage?.(t('musicSystem.playback.selectSpeaker'), 'error');
      return;
    }
    if (onPlayUri) {
      try {
        await onPlayUri(uri, { addToQueue });
        if (addToQueue) onMessage?.(t('musicSystem.search.addedToQueue'), 'success');
      } catch (e: any) {
        onMessage?.(e?.response?.data?.detail || e?.message || t('musicSystem.playback.playError'), 'error');
      }
      return;
    }
    if (!speakerId) {
      onMessage?.(t('musicSystem.playback.selectSpeaker'), 'error');
      return;
    }
    try {
      await playSpotifyTrack({ track_uri: uri, target_speaker_id: speakerId, crossfade, add_to_queue: addToQueue });
      if (addToQueue) {
        onMessage?.(t('musicSystem.search.addedToQueue'), 'success');
      }
    } catch (e: any) {
      onMessage?.(e?.response?.data?.detail || t('musicSystem.playback.playError'), 'error');
    }
  };

  const toggleLike = async (id: string) => {
    if (blockDemo()) return;
    try {
      if (liked.has(id)) {
        await spotifyUnlikeTrack([id]);
        setLiked((prev) => { const n = new Set(prev); n.delete(id); return n; });
        onLibraryChanged?.();
      } else {
        await spotifyLikeTrack([id]);
        setLiked((prev) => new Set(prev).add(id));
        onLibraryChanged?.();
      }
    } catch { /* noop */ }
  };

  const addToQueue = async (uri: string) => {
    if (blockDemo()) return;
    if (!canControl) {
      onMessage?.(t('musicSystem.playback.selectSpeaker'), 'error');
      return;
    }
    if (onQueueUri || onPlayUri) {
      try {
        if (onQueueUri) await onQueueUri(uri);
        else await onPlayUri?.(uri, { addToQueue: true });
        onMessage?.(t('musicSystem.search.addedToQueue'), 'success');
      } catch (e: any) {
        onMessage?.(e?.response?.data?.detail || e?.message || t('musicSystem.playback.playError'), 'error');
      }
      return;
    }
    if (!speakerId) {
      onMessage?.(t('musicSystem.playback.selectSpeaker'), 'error');
      return;
    }
    try {
      await queueAdd(speakerId, { uri });
      onMessage?.(t('musicSystem.search.addedToQueue'), 'success');
    } catch (e: any) {
      onMessage?.(e?.response?.data?.detail || t('musicSystem.playback.playError'), 'error');
    }
  };

  const openAddToPlaylist = (uri: string) => {
    setAddToPlaylistUri(uri);
    setAddToPlaylistOpen(true);
  };

  const showArtistTracks = async (artist: { name: string }) => {
    setArtistFilter(artist.name);
    setQuery(artist.name);
    await handleSearch(`artist:"${artist.name}"`, 'track');
  };

  const typeTrackLabel = t('musicSystem.search.typeTrack');
  const typeAlbumLabel = t('musicSystem.search.typeAlbum');
  const typePlaylistLabel = t('musicSystem.search.typePlaylist');
  const typeArtistLabel = t('musicSystem.search.typeArtist');

  const tracks = results?.tracks?.items?.filter(Boolean) ?? [];
  const playlists = results?.playlists?.items?.filter(Boolean) ?? [];
  const albums = results?.albums?.items?.filter(Boolean) ?? [];
  const artists = results?.artists?.items?.filter(Boolean) ?? [];
  const hasResults = tracks.length + playlists.length + albums.length + artists.length > 0;

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        value={query}
        placeholder={t('musicSystem.playback.searchPlaceholder')}
        onChange={(e) => { setArtistFilter(null); setQuery(e.target.value); }}
        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
        sx={{ mb: 1, input: { color: '#fff' }, '.MuiOutlinedInput-notchedOutline': { borderColor: '#444' } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconButton size="small" onClick={() => handleSearch()} sx={{ color: '#999', p: 0.25 }}>
                <SearchIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </InputAdornment>
          ),
          endAdornment: loading ? <CircularProgress size={16} sx={{ color: SONOS_BLUE }} /> : undefined,
        }}
      />

      {artistFilter && (
        <Typography sx={{ color: '#999', fontSize: 11, mb: 0.75 }}>
          {t('musicSystem.search.typeArtist')}: {artistFilter}
        </Typography>
      )}

      {searchError && (
        <Typography sx={{ color: '#e57373', fontSize: 12, py: 0.5, mb: 0.5 }}>
          {searchError}
        </Typography>
      )}

      {searched && !loading && !searchError && !hasResults && (
        <Typography sx={{ color: '#999', fontSize: 12, py: 1 }}>
          {t('musicSystem.search.noResults')}
        </Typography>
      )}

      {tracks.map((tr: any) => (
        <SearchResultRow
          key={`tr-${tr.id}`}
          imageUrl={spotifyImageUrl(tr.album)}
          title={tr.name}
          subtitle={tr.artists?.map((a: any) => a.name).join(', ')}
          actions={(
            <>
              <TypeBadge label={typeTrackLabel} />
              <Tooltip title={liked.has(tr.id) ? t('musicSystem.search.unlikeTrack') : t('musicSystem.search.likeTrackHint')}>
                <IconButton size="small" onClick={() => toggleLike(tr.id)} sx={{ color: liked.has(tr.id) ? '#e91e63' : '#666', ...TRACK_ACTION_ICON_SX }}>
                  {liked.has(tr.id) ? <Favorite sx={{ fontSize: 20 }} /> : <FavoriteBorder sx={{ fontSize: 20 }} />}
                </IconButton>
              </Tooltip>
              <Tooltip title={t('musicSystem.search.addToQueue')}>
                <span>
                  <IconButton size="small" onClick={() => addToQueue(tr.uri)} sx={{ color: canControl ? '#aaa' : '#555', ...TRACK_ACTION_ICON_SX }}>
                    <PlaylistAdd sx={{ fontSize: 22 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('musicSystem.search.addToPlaylist')}>
                <span>
                  <IconButton size="small" onClick={() => openAddToPlaylist(tr.uri)} sx={{ color: '#aaa', ...TRACK_ACTION_ICON_SX }}>
                    <LibraryAdd sx={{ fontSize: 22 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('musicSystem.playlists.detail.playPlaylist')}>
                <span>
                  <TrackPlayButton size="compact" disabled={!canControl} onClick={() => playTrack(tr.uri)} />
                </span>
              </Tooltip>
            </>
          )}
        />
      ))}

      {playlists.map((pl: any) => (
        <SearchResultRow
          key={`pl-${pl.id}`}
          imageUrl={spotifyImageUrl(pl)}
          title={pl.name}
          actions={(
            <>
              <TypeBadge label={typePlaylistLabel} />
              <Tooltip title={savedPlaylists.has(pl.id)
                ? t('musicSystem.search.playlistAlreadyInLibrary')
                : t('musicSystem.search.addPlaylistToLibrary')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => addPlaylistToLib(pl)}
                    disabled={cloningPlaylists.has(pl.id)}
                    sx={{ color: savedPlaylists.has(pl.id) ? SONOS_BLUE : '#aaa', ...TRACK_ACTION_ICON_SX }}
                  >
                    {cloningPlaylists.has(pl.id)
                      ? <CircularProgress size={18} sx={{ color: SONOS_BLUE }} />
                      : savedPlaylists.has(pl.id)
                        ? <LibraryAddCheck sx={{ fontSize: 22 }} />
                        : <LibraryAdd sx={{ fontSize: 22 }} />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('musicSystem.playlists.detail.playPlaylist')}>
                <span>
                  <TrackPlayButton
                    size="compact"
                    disabled={!canControl}
                    onClick={() => onPlayPlaylist?.(pl.uri, pl.name)}
                  />
                </span>
              </Tooltip>
            </>
          )}
        />
      ))}

      {albums.map((al: any) => (
        <SearchResultRow
          key={`al-${al.id}`}
          imageUrl={spotifyImageUrl(al)}
          title={al.name}
          subtitle={al.artists?.map((a: any) => a.name).join(', ')}
          actions={(
            <>
              <TypeBadge label={typeAlbumLabel} />
              <Tooltip title={savedAlbums.has(al.id)
                ? t('musicSystem.search.removeAlbumFromLibrary')
                : t('musicSystem.search.addAlbumToLibrary')}>
                <IconButton
                  size="small"
                  onClick={() => toggleAlbumLibrary(al)}
                  sx={{ color: savedAlbums.has(al.id) ? SONOS_BLUE : '#aaa', ...TRACK_ACTION_ICON_SX }}
                >
                  {savedAlbums.has(al.id) ? <LibraryAddCheck sx={{ fontSize: 22 }} /> : <LibraryAdd sx={{ fontSize: 22 }} />}
                </IconButton>
              </Tooltip>
              <Tooltip title={t('musicSystem.search.addToQueue')}>
                <span>
                  <IconButton size="small" onClick={() => addToQueue(al.uri)} sx={{ color: canControl ? '#aaa' : '#555', ...TRACK_ACTION_ICON_SX }}>
                    <PlaylistAdd sx={{ fontSize: 22 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('musicSystem.playlists.detail.playPlaylist')}>
                <span>
                  <TrackPlayButton
                    size="compact"
                    disabled={!canControl}
                    onClick={() => onPlayPlaylist?.(al.uri, al.name)}
                  />
                </span>
              </Tooltip>
            </>
          )}
        />
      ))}

      {artists.map((ar: any) => (
        <SearchResultRow
          key={`ar-${ar.id}`}
          imageUrl={spotifyImageUrl(ar)}
          title={ar.name}
          onClick={() => { void showArtistTracks(ar); }}
          actions={(
            <>
              <TypeBadge label={typeArtistLabel} />
              <Tooltip title={t('musicSystem.search.artistTracksHint')}>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); void showArtistTracks(ar); }}
                  sx={{ color: SONOS_BLUE, ...TRACK_ACTION_ICON_SX }}
                >
                  <ChevronRight sx={{ fontSize: 26 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        />
      ))}

      <AddToPlaylistDialog
        open={addToPlaylistOpen}
        trackUri={addToPlaylistUri}
        onClose={() => { setAddToPlaylistOpen(false); setAddToPlaylistUri(null); }}
        onAdded={() => onMessage?.(t('musicSystem.search.addedToPlaylist'), 'success')}
        onError={(msg) => onMessage?.(msg, 'error')}
      />
    </Box>
  );
};

export default SpotifySearchPanel;
